import { Field, Form, Formik } from "formik";
import Modal from "react-responsive-modal";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useToasts } from "react-toast-notifications";
import WellnessService from "services/WellnessService";
import CustomInput from "components/CustomInput/CustomInput";
import ButtonWithProgress from "components/ButtonWithProgress";
import CustomButton from "components/CustomButton";
import { ReactComponent as CloseIcon } from "assets/img/icons/cancel.svg";
import classNames from "classnames";
import Loader from "components/Loader";

const EditTrainingDocument = ({ onClose, document, onUpdate }) => {
  const params = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [docData, setDocData] = useState(null);
  const { addToast } = useToasts();

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const res = await WellnessService.getTrainingDocumentById(params.teamId, document.id);
        setDocData(res?.data?.data);
        setIsLoading(false);
      } catch (e) {
        console.log(e);
        addToast("Failed to fetch document details.", { appearance: "error" });
        setIsLoading(false);
        onClose();
      }
    })();
  }, [params.teamId, document.id, addToast, onClose]);

  const formSchema = Yup.object().shape({
    title: Yup.string().trim().required("Please add a title."),
    short_desc: Yup.string()
      .trim()
      .min(4, "Short description must be at least 4 characters long.")
      .max(250, "Short description must be 250 characters or less.")
      .required("Please add a short description."),
  });

  const submitForm = async (values) => {
    try {
      setSubmitting(true);
      const res = await WellnessService.updateTrainingDocument(
        params.teamId,
        document.id,
        { title: values.title, short_desc: values.short_desc }
      );
      addToast("Document updated successfully.", { appearance: "success" });
      if (onUpdate) onUpdate(res?.data?.data ?? { ...document, ...values });
      setSubmitting(false);
      onClose();
    } catch (e) {
      console.log(e);
      setSubmitting(false);
      addToast("An error occurred while updating the document.", { appearance: "error" });
    }
  };

  return (
    <Modal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      classNames={{ modalContainer: "modalContainer", modal: "createTaskModal" }}
      open
      showCloseIcon={false}
      onClose={onClose}
    >
      <div className="modal">
        <h5 className="text-center createTaskHeading">Edit Training Document</h5>
        <CustomButton type="button" buttonClass="btnClose">
          <CloseIcon style={{ width: "20px" }} onClick={onClose} />
        </CustomButton>
        {isLoading ? (
          <div className="d-flex justify-content-center p-5">
            <Loader />
          </div>
        ) : (
          <Formik
            enableReinitialize
            initialValues={{ title: docData?.title ?? "", short_desc: docData?.short_desc ?? "" }}
            validationSchema={formSchema}
            onSubmit={submitForm}
          >
            <Form>
              <Field name="title">
                {({ field, form }) => (
                  <CustomInput
                    inputProps={{
                      placeholder: "Title",
                      name: "title",
                      value: field.value,
                      error:
                        !!form.errors.title && !!form.touched.title ? form.errors.title : null,
                    }}
                    inputClass="create-text-padding uploadModalInput"
                    onChange={field.onChange}
                  />
                )}
              </Field>
              <Field name="short_desc">
                {({ field, form }) => (
                  <>
                    <textarea
                      placeholder="Short Description"
                      name="short_desc"
                      onChange={field.onChange}
                      value={field.value}
                      rows={5}
                      className={classNames({
                        "user-profile-inputs": true,
                        "create-text-padding": true,
                        uploadModalTextArea: true,
                      })}
                      style={
                        !!form.errors.short_desc && !!form.touched.short_desc
                          ? { borderWidth: "2px", borderColor: "#FF0000" }
                          : null
                      }
                    />
                    {form?.errors?.short_desc && (
                      <span className="errorMessage">{form.errors.short_desc}</span>
                    )}
                  </>
                )}
              </Field>
              <ButtonWithProgress
                initialText={"Update"}
                isSubmitting={submitting}
                buttonWithProgressClass="btn grad_btn full_w mt-3"
              />
            </Form>
          </Formik>
        )}
      </div>
    </Modal>
  );
};

export default EditTrainingDocument;
